/* 分析工作台（Workspace Hub）— 全帳號共用的分析工作台（2026-08-24 統一）；
 * 外部分析帳號（analyst / analyst_lite）的登入落地頁，員工/non_staff 由側欄
 * Primary、AppSwitcher 或越權 redirect 進入（員工落地頁仍是交易紀錄）。
 *
 * 四區：
 *   1. 問候列 — serif 大字「早安/午安/晚安，{顯示名}。」+ 日期
 *   2. 今日情報 — 三張橫排入口卡（盤前多空重點 / 早晨報告·分析報告 / 市場新聞），
 *      各做一個輕量 fetch 取「今日更新數/最新時間」，任何失敗靜默降級成純入口卡
 *   3. 個股快查 — 重用 hr-stock-studio.jsx 的 StockSearchBox 與 HOT_STOCKS
 *      （window 全域，勿複製），選中個股導向個股工作室
 *   4. 最近使用（GSRecent localStorage helper，hr-shell.jsx）+ 全部工具卡片牆
 *      （GET /api/tools manifest，依 category 三段渲染；員工會多拿到
 *      privileged_only 的研究引擎卡，非員工 manifest 不下發該卡）
 *
 * 依賴（window globals，載入順序見 index.html）：HR / FontSizes / api / useToast /
 * GSRecent / StockSearchBox / HOT_STOCKS / openQuantDataTab / openInsiderTab /
 * openYtkbTab / openResearchEngineTab / openScannerTab / todayStr。
 * 路由：#/hub（_router.jsx ROUTE_PAGES + hr-shell.jsx EXTRA_ROUTES；受限帳號
 *       白名單 _ANALYST_ROUTES / _NON_STAFF_ROUTES 皆含 hub）。
 * 權限：全帳號可達（員工走 canAccessRoute generic 規則、非員工走白名單）。
 */

(function () {

/* ─────────────  問候與日期  ───────────── */

function hubGreeting(d) {
  const h = d.getHours();
  if (h >= 5 && h < 11) return '早安';
  if (h >= 11 && h < 18) return '午安';
  return '晚安';
}

const _WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];
function hubDateLabel(d) {
  return `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日 · 星期${_WEEKDAYS[d.getDay()]}`;
}

function displayName(user) {
  if (!user) return '';
  return user.employee_name || user.display_name || (user.username || '').split('@')[0];
}

/* ─────────────  區塊標題（uppercase eyebrow，站內慣例）───────────── */

function SectionTitle({ children, right }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      margin: '0 0 12px',
    }}>
      <div style={uiStyle({
        fontSize: FontSizes.trading_lg, letterSpacing: '0.2em', textTransform: 'uppercase',
        color: HR.inkFaint,
      }, {
        // mockup .ch h2：17px／800、無大寫字距
        fontSize: GxT.size.h3, fontWeight: GxT.weight.title, letterSpacing: 0,
        textTransform: 'none', color: GxT.color.on,
      })}>{children}</div>
      {right}
    </div>
  );
}

/* ─────────────  今日情報 — 輕量 fetch（全部靜默降級）───────────── */

function useTodayIntel() {
  const [intel, setIntel] = React.useState({ focus: null, reports: null, news: null });
  React.useEffect(() => {
    let alive = true;
    const today = (typeof todayStr === 'function') ? todayStr() : new Date().toLocaleDateString('sv-SE');
    const set = (key, val) => { if (alive) setIntel((s) => ({ ...s, [key]: val })); };

    // 盤前多空重點 — 最新一篇的日期（/api/morning-focus/list，hr-morning-focus.jsx 同款）
    api('GET', '/morning-focus/list')
      .then((rows) => {
        const top = Array.isArray(rows) && rows.length ? rows[0] : null;
        if (!top || !top.date) return;
        set('focus', top.date === today ? '今日已更新' : `最新 ${top.date}`);
      })
      .catch(() => { /* 靜默降級成純入口卡 */ });

    // 早晨報告 / 分析報告 — 今日份數（/api/ocr-reports?date=，hr-analysis-report.jsx 同款）
    api('GET', `/ocr-reports?date=${today}`)
      .then((raw) => {
        const list = Array.isArray(raw) ? raw : (raw && raw.reports) || [];
        if (list.length) set('reports', `今日 ${list.length} 份報告`);
      })
      .catch(() => { /* 靜默 */ });

    // 市場新聞 — 今日則數 + 最新時間（/api/news，hr-daily-brief.jsx 同款）
    api('GET', '/news?limit=50')
      .then((items) => {
        if (!Array.isArray(items)) return;
        const todays = items.filter((n) => {
          const d = n && n.published_at ? new Date(n.published_at) : null;
          return d && !isNaN(d) && d.toLocaleDateString('sv-SE') === today;
        });
        if (!todays.length) return;
        const latest = todays.reduce((a, b) =>
          new Date(a.published_at) > new Date(b.published_at) ? a : b);
        const t = new Date(latest.published_at);
        const hh = String(t.getHours()).padStart(2, '0');
        const mm = String(t.getMinutes()).padStart(2, '0');
        set('news', `今日 ${todays.length} 則 · 最新 ${hh}:${mm}`);
      })
      .catch(() => { /* 靜默 */ });

    return () => { alive = false; };
  }, []);
  return intel;
}

function IntelCard({ title, desc, sub, hash, onGo }) {
  const [hover, setHover] = React.useState(false);
  return (
    <a
      href={hash}
      onClick={onGo}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={uiStyle({
        display: 'block', textDecoration: 'none',
        background: HR.surface, borderRadius: 8, padding: '16px 18px',
        border: `1px solid ${hover ? HR.accent : HR.rule}`,
        transition: 'border-color 120ms',
      }, {
        // 無框卡片；hover 改 tonal 底（mockup 卡片無外框）
        border: 'none', borderRadius: GxT.radius.card, padding: '18px 20px',
        background: hover ? GxT.color.s1 : GxT.color.card,
        transition: 'background 160ms',
      })}
    >
      <div style={uiStyle({
        fontFamily: HR.serif, fontSize: FontSizes.md, fontWeight: 500,
        color: HR.ink, letterSpacing: '-0.01em', marginBottom: 4,
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      }, { fontSize: GxT.size.h3, fontWeight: GxT.weight.title, letterSpacing: 0, marginBottom: 6 })}>
        <span>{title}</span>
        <span style={{ color: hover ? HR.accent : HR.inkFaint, fontSize: FontSizes.xs }}>→</span>
      </div>
      <div style={uiStyle(
        { color: HR.inkSoft, fontSize: FontSizes.xxs, lineHeight: 1.5 },
        { fontSize: 14 },
      )}>{desc}</div>
      <div style={uiStyle({
        marginTop: 10, fontSize: FontSizes.trading_xl, letterSpacing: '0.04em',
        color: sub ? HR.accent : HR.inkFaint, fontWeight: sub ? 600 : 400,
      }, { fontSize: 13, letterSpacing: 0, fontWeight: sub ? GxT.weight.label : GxT.weight.medium })}>
        {sub || '點擊進入'}
      </div>
    </a>
  );
}

/* ─────────────  工具卡片（manifest 驅動）───────────── */

function ToolBadge({ text, tone }) {
  const styles = tone === 'new'
    ? { background: HR.warn, color: '#fff', fontWeight: 700 }
    : { background: HR.surfaceAlt, color: HR.inkFaint, border: `1px solid ${HR.ruleSoft}` };
  // GX：tonal 標籤（mockup .gx-chip.new／neutral），圓角 6、無框
  const gx = GxCore.tone(tone === 'new' ? 'new' : 'neutral');
  return (
    <span style={uiStyle({
      fontSize: FontSizes.trading_lg, padding: '2px 8px', borderRadius: 3,
      letterSpacing: '0.08em', whiteSpace: 'nowrap', ...styles,
    }, {
      borderRadius: GxT.radius.chipSm, border: 'none', letterSpacing: 0,
      fontWeight: GxT.weight.label, background: gx.bg, color: gx.fg,
    })}>{text}</span>
  );
}

function ToolCard({ tool, onActivate }) {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      type="button"
      onClick={() => onActivate(tool)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={uiStyle({
        display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left',
        background: HR.surface, borderRadius: 8, padding: '18px 20px',
        minHeight: 148, width: '100%',
        border: `1px solid ${hover ? HR.accent : HR.rule}`,
        boxShadow: hover ? '0 2px 10px rgba(14,20,17,0.06)' : 'none',
        transform: hover ? 'translateY(-1px)' : 'none',
        cursor: 'pointer', fontFamily: HR.sans,
        transition: 'border-color 120ms, box-shadow 120ms, transform 120ms',
      }, {
        // 無框卡片 16；hover 改 tonal 底、不位移（mockup .va2-cat-tile GX 規則同款）
        border: 'none', borderRadius: GxT.radius.card, padding: 20,
        background: hover ? GxT.color.s1 : GxT.color.card,
        boxShadow: 'none', transform: 'none', transition: 'background 160ms',
      })}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
        <div style={uiStyle({
          width: 42, height: 42, borderRadius: 9, flexShrink: 0,
          background: HR.accentSoft, color: HR.accent,
          display: 'grid', placeItems: 'center',
          fontFamily: HR.serif, fontSize: 19, fontWeight: 600,
        }, { width: 40, height: 40, borderRadius: GxT.radius.btnSm, color: GxT.color.onPc, fontWeight: GxT.weight.display })}>{(tool.label || '?').charAt(0)}</div>
        <span style={uiStyle({
          fontSize: FontSizes.base, fontWeight: 600, color: HR.ink,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }, { fontSize: 16, fontWeight: GxT.weight.title })}>{tool.label}</span>
        {tool.is_new && <ToolBadge text="NEW" tone="new" />}
      </div>
      <div style={uiStyle(
        { flex: 1, color: HR.inkSoft, fontSize: FontSizes.xs, lineHeight: 1.6 },
        { fontSize: 14 },
      )}>{tool.desc}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {tool.mode === 'tab' ? <ToolBadge text="新分頁 ↗" /> : <ToolBadge text="站內" />}
        <span style={{
          marginLeft: 'auto', fontSize: FontSizes.xs, fontWeight: 600,
          color: HR.accent, opacity: hover ? 1 : 0, transition: 'opacity 120ms',
        }}>開啟 →</span>
      </div>
    </button>
  );
}

/* ─────────────  頁面本體  ───────────── */

function AnalystHubPage() {
  const user = window._USER;
  const toast = (typeof useToast === 'function') ? useToast() : null;
  const now = React.useMemo(() => new Date(), []);
  const intel = useTodayIntel();

  // /api/tools manifest：null=載入中、'error'=失敗（顯示安靜提示）、object=就緒
  const [manifest, setManifest] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    api('GET', '/tools')
      .then((data) => { if (alive) setManifest(data && Array.isArray(data.tools) ? data : 'error'); })
      .catch(() => { if (alive) setManifest('error'); });
    return () => { alive = false; };
  }, []);

  const [recent, setRecent] = React.useState(() =>
    (window.GSRecent ? window.GSRecent.list() : []));

  const catLabel = React.useMemo(() => {
    const m = { daily: '每日情報', equity: '個股與量化', research: '深度研究' };
    if (manifest && manifest !== 'error') {
      (manifest.categories || []).forEach((c) => { m[c.key] = c.label; });
    }
    return m;
  }, [manifest]);

  const pushRecent = (entry) => {
    if (window.GSRecent) {
      window.GSRecent.push(entry);
      setRecent(window.GSRecent.list());
    }
  };

  // tab 型工具 → 既有「直開新分頁」handler（hr-shell.jsx open*Tab 系，品牌過場）；
  // scanner 優先走 openScannerTab（manifest 取址），無 handler 才 fallback
  // 直開 manifest 的 external_url。
  const openTabTool = (routeKey, externalUrl) => {
    if (routeKey === 'quantdata' && window.openQuantDataTab) return window.openQuantDataTab(toast);
    if (routeKey === 'insider' && window.openInsiderTab) return window.openInsiderTab(toast);
    if (routeKey === 'txo-board' && window.openTxoBoardTab) return window.openTxoBoardTab(toast);
    if (routeKey === 'fund-radar' && window.openFundRadarTab) return window.openFundRadarTab(toast);
    if (routeKey === 'bigday' && window.openBigdayTab) return window.openBigdayTab(toast);
    if (routeKey === 'market-calendar' && window.openMarketCalendarTab) return window.openMarketCalendarTab(toast);
    if (routeKey === 'ytkb' && window.openYtkbTab) return window.openYtkbTab(toast);
    if (routeKey === 'research-engine' && window.openResearchEngineTab) return window.openResearchEngineTab(toast);
    if (routeKey === 'kelly-game' && window.openKellyGameTab) return window.openKellyGameTab(toast);
    if (routeKey === 'tjcoach' && window.openTJCoachTab) return window.openTJCoachTab(toast);
    if (routeKey === 'expert-tracker' && window.openExpertTrackerTab) return window.openExpertTrackerTab(toast);
    if (routeKey === 'scanner' && window.openScannerTab) return window.openScannerTab(toast);
    if (externalUrl) { window.open(externalUrl, '_blank', 'noopener'); return; }
    if (toast) toast('此工具暫時無法開啟', 'error');
  };

  const activateTool = (t) => {
    const sub = catLabel[t.category] || '';
    if (t.mode === 'tab') {
      pushRecent({ label: t.label, sub, hash: '', tab: t.route, url: t.external_url || undefined });
      openTabTool(t.route, t.external_url);
    } else {
      pushRecent({ label: t.label, sub, hash: `#/${t.route}` });
      window.location.hash = `#/${t.route}`;
    }
  };

  const openRecent = (entry) => {
    if (entry.tab) { openTabTool(entry.tab, entry.url); return; }
    if (entry.hash) window.location.hash = entry.hash;
  };

  const recentTop = recent.slice(0, 6);
  const StockSearch = window.StockSearchBox;
  const hotStocks = Array.isArray(window.HOT_STOCKS) ? window.HOT_STOCKS : [];

  return (
    <>
      {/* 1) 問候列 — PageHeader 風格（eyebrow + serif 大字 + 底線） */}
      <div style={uiStyle(
        { paddingBottom: 22, borderBottom: `2px solid ${HR.ink}`, marginBottom: 28 },
        // mockup .greet：無底線，留白分段
        { padding: '4px 4px 0', borderBottom: 'none', marginBottom: 28 },
      )}>
        <div style={uiStyle({
          fontSize: FontSizes.trading_lg, letterSpacing: '0.24em', textTransform: 'uppercase',
          color: HR.inkFaint, marginBottom: 8,
        }, {
          // .gdate：主色 700、無大寫字距
          fontSize: GxT.size.label, letterSpacing: 0, textTransform: 'none',
          color: GxT.color.p, fontWeight: GxT.weight.label, marginBottom: 4,
        })}>Workspace · 分析工作台</div>
        <h1 style={uiStyle({
          fontFamily: HR.serif, fontSize: FontSizes.xl, fontWeight: 400,
          letterSpacing: '-0.02em', lineHeight: 1.05, margin: 0, color: HR.ink,
        }, {
          // .greet h1：900／clamp
          fontSize: 'clamp(28px, 6vw, 40px)', fontWeight: GxT.weight.display,
          letterSpacing: '-0.5px', lineHeight: 1.12,
        })}>{hubGreeting(now)}，{displayName(user)}。</h1>
        <div style={uiStyle(
          { color: HR.inkFaint, fontSize: FontSizes.xxs, marginTop: 10 },
          { color: GxT.color.onV, fontSize: GxT.size.body, marginTop: 6 },
        )}>
          {hubDateLabel(now)}
        </div>
      </div>

      {/* 2) 今日情報 — 三張橫排入口卡（fetch 失敗自動變純入口卡） */}
      <div style={{ marginBottom: 32 }}>
        <SectionTitle>今日情報</SectionTitle>
        <div style={{
          display: 'grid', gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        }}>
          <IntelCard
            title="盤前多空重點" desc="華南投顧盤前多空新聞，利多利空一眼掌握"
            sub={intel.focus} hash="#/morning-focus"
            onGo={() => pushRecent({ label: '盤前重點', sub: '每日情報', hash: '#/morning-focus' })}
          />
          <IntelCard
            title="早晨報告 · 研究報告庫" desc="每日晨報與券商研究報告，站內直接閱讀"
            sub={intel.reports} hash="#/morning-report"
            onGo={() => pushRecent({ label: '早晨報告', sub: '每日情報', hash: '#/morning-report' })}
          />
          <IntelCard
            title="市場新聞" desc="台股市場新聞看板，依來源與關鍵字瀏覽"
            sub={intel.news} hash="#/daily-brief"
            onGo={() => pushRecent({ label: '市場新聞', sub: '每日情報', hash: '#/daily-brief' })}
          />
        </div>
      </div>

      {/* 3) 個股快查 — 重用個股工作室的搜尋框與熱門捷徑 */}
      <div style={{ marginBottom: 32 }}>
        <SectionTitle>個股快查</SectionTitle>
        <div style={uiStyle({
          background: HR.surface, border: `1px solid ${HR.rule}`, borderRadius: 8,
          padding: '18px 20px',
        }, { border: 'none', borderRadius: GxT.radius.card, padding: 20 })}>
          {StockSearch ? (
            <>
              <StockSearch
                compact
                onPick={(hit) => pushRecent({
                  label: `${hit.symbol} ${hit.name || ''}`.trim(),
                  sub: '個股工作室',
                  hash: `#/stock-studio/${hit.symbol}`,
                })}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                {hotStocks.map(([s, n]) => (
                  <a
                    key={s} href={`#/stock-studio/${s}`}
                    onClick={() => pushRecent({ label: `${s} ${n}`, sub: '個股工作室', hash: `#/stock-studio/${s}` })}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = HR.accent; e.currentTarget.style.color = HR.accent; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = HR.rule; e.currentTarget.style.color = HR.inkSoft; }}
                    style={uiStyle({
                      border: `1px solid ${HR.rule}`, borderRadius: 99, padding: '4px 12px',
                      color: HR.inkSoft, fontSize: FontSizes.xxs, background: HR.surfaceAlt,
                      textDecoration: 'none', transition: 'all 120ms',
                    }, {
                      // mockup .per：外框 chip、圓角 8
                      borderRadius: GxT.radius.chip, padding: '6px 12px', fontSize: 13,
                      fontWeight: GxT.weight.medium, background: 'transparent',
                    })}
                  >{s} {n}</a>
                ))}
              </div>
            </>
          ) : (
            // StockSearchBox 未載到（hr-stock-studio.js 載入失敗）→ 純入口降級
            <a href="#/stock-studio" style={{ color: HR.accent, fontSize: FontSizes.xs, textDecoration: 'none' }}>
              前往個股工作室搜尋 →
            </a>
          )}
        </div>
      </div>

      {/* 4a) 最近使用 — 空清單整區隱藏 */}
      {recentTop.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <SectionTitle>最近使用</SectionTitle>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {recentTop.map((e, i) => (
              <button
                key={`${e.label}-${i}`}
                type="button"
                onClick={() => openRecent(e)}
                onMouseEnter={(ev) => { ev.currentTarget.style.borderColor = HR.accent; }}
                onMouseLeave={(ev) => { ev.currentTarget.style.borderColor = HR.rule; }}
                style={uiStyle({
                  display: 'flex', alignItems: 'baseline', gap: 8,
                  border: `1px solid ${HR.rule}`, borderRadius: 8, padding: '7px 14px',
                  background: HR.surface, cursor: 'pointer', fontFamily: HR.sans,
                  transition: 'border-color 120ms',
                }, { alignItems: 'center', minHeight: 40, padding: '0 16px', borderRadius: GxT.radius.btn })}
              >
                <span style={uiStyle(
                  { fontSize: FontSizes.xxs, fontWeight: 600, color: HR.ink },
                  { fontSize: 14, fontWeight: GxT.weight.label },
                )}>{e.label}</span>
                {e.sub && <span style={{ fontSize: FontSizes.trading_md, color: HR.inkFaint }}>{e.sub}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 4b) 全部工具 — /api/tools manifest 依 category 三段渲染 */}
      <div style={{ marginBottom: 24 }}>
        <SectionTitle>全部工具</SectionTitle>
        {manifest === null && (
          <div style={{ color: HR.inkFaint, fontSize: FontSizes.xxs, padding: '18px 0' }}>載入中…</div>
        )}
        {manifest === 'error' && (
          <div style={{ color: HR.inkFaint, fontSize: FontSizes.xxs, padding: '18px 0' }}>
            工具清單暫時載入失敗 — 側欄項目仍可正常使用。
          </div>
        )}
        {manifest && manifest !== 'error' && (manifest.categories || []).map((cat) => {
          const tools = (manifest.tools || []).filter((t) => t.category === cat.key);
          if (!tools.length) return null;
          return (
            <div key={cat.key} style={{ marginBottom: 26 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '0 0 12px' }}>
                <span style={uiStyle({
                  fontFamily: HR.serif, fontSize: FontSizes.md, fontWeight: 600, color: HR.ink,
                }, { fontSize: 15, fontWeight: GxT.weight.title })}>{cat.label}</span>
                <span style={{
                  fontFamily: HR.mono, fontSize: FontSizes.trading_md, color: HR.inkFaint,
                }}>{tools.length}</span>
                <span style={{ flex: 1, height: 1, background: HR.rule, alignSelf: 'center' }} />
              </div>
              <div style={{
                display: 'grid', gap: 12,
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              }}>
                {tools.map((t) => <ToolCard key={t.key} tool={t} onActivate={activateTool} />)}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

window.AnalystHubPage = AnalystHubPage;

})();
